import type {
  ActivityDayPoint,
  EngagementComparison,
  PlayerOwnership,
  RosterInjuryNews,
  SigningPlayerComparison,
  TopPlayer,
  TimeWindow,
} from '../data/types';
import { ROSTER_MOVES_TRACKED_SINCE } from '../data/types';
import {
  contentPriorityScore,
  detectExceptionalActivity,
  detectPoolGrowth,
} from '../data/metrics';
import { PERIOD_PHRASES, formatLongDay, formatNumber } from '../data/overviewStory';

/**
 * Opportunités de contenu pour les gestionnaires de médias. Chaque texte ne
 * reformule que des valeurs reçues des RPC `ci_*` : aucune IA, aucune
 * projection, aucun emoji ni hashtag. Les noms de RPC restent dans `sources`
 * (affichage technique) et n'apparaissent jamais dans le texte copié.
 */

export type ContentFormat = 'short' | 'social' | 'article' | 'newsletter';

export type OpportunityCategory = 'signings' | 'injuries' | 'roster' | 'activity';

export const CATEGORY_LABELS: Record<OpportunityCategory, string> = {
  signings: 'Signatures',
  injuries: 'Blessures et retours',
  roster: 'Mouvements d’effectif',
  activity: 'Activité du pool',
};

/** Phrase de méthode ajoutée au format Article. */
const CATEGORY_METHOD: Record<OpportunityCategory, string> = {
  signings: 'Seules les signatures d’agents libres sont comptées.',
  injuries: 'Nouvelle publiée par la LNH, croisée avec les rosters actuels du pool.',
  roster: `Libérations et mises en LTIR suivies depuis le ${ROSTER_MOVES_TRACKED_SINCE}.`,
  activity: 'Compteurs tirés de l’activité enregistrée dans le pool.',
};

/** Limite du format Court (publication sur X, extraits). */
export const SHORT_FORMAT_LIMIT = 280;

export interface OpportunityFact {
  label: string;
  value: string;
}

export interface ContentOpportunity {
  /** Clé unique et stable (type + période ou joueur), utilisée pour « déjà utilisée ». */
  id: string;
  /** Type de détection, indépendant du joueur et de la période. */
  kind: string;
  category: OpportunityCategory;
  title: string;
  /** Constat factuel principal. */
  reason: string;
  /** Piste éditoriale factuelle (détention, contexte du pool), si disponible. */
  angle: string | null;
  facts: OpportunityFact[];
  period: string;
  playerId: number | null;
  playerName: string | null;
  /** Mention publique de la source, incluse dans le texte copié. */
  attribution: string;
  /** RPC et tables techniques, jamais dans le texte copié. */
  sources: string[];
  score: number;
  priority: 'Élevée' | 'Moyenne' | 'Faible';
  eventTime: string | null;
}

export interface OpportunityContext {
  window: TimeWindow;
  leagueName?: string | null;
  /** Détention par joueur (ci_get_player_ownership), facultative. */
  ownership?: Map<number, PlayerOwnership>;
  now?: number;
}

function priorityLabel(score: number): ContentOpportunity['priority'] {
  if (score >= 70) return 'Élevée';
  if (score >= 40) return 'Moyenne';
  return 'Faible';
}

function plural(count: number, singular: string, pluralForm: string): string {
  return `${formatNumber(count)} ${Math.abs(count) > 1 ? pluralForm : singular}`;
}

function playerLabel(name: string | null | undefined, id: number): string {
  return name?.trim() || `Joueur #${id}`;
}

function today(ctx: OpportunityContext): string {
  return new Date(ctx.now ?? Date.now()).toISOString().slice(0, 10);
}

function poolAttribution(ctx: OpportunityContext, withNhl = false): string {
  const pool = ctx.leagueName?.trim() ? `du pool ${ctx.leagueName.trim()}` : 'du pool';
  return withNhl
    ? `Source : nouvelles de la LNH et rosters ${pool}.`
    : `Source : données ${pool}.`;
}

function ownershipOf(ctx: OpportunityContext, playerId: number | null): PlayerOwnership | null {
  if (playerId === null || !ctx.ownership) return null;
  const row = ctx.ownership.get(playerId);
  return row && row.total_poolers > 0 ? row : null;
}

/** Part des équipes (0-100) arrondie à l'unité. */
export function ownershipShare(row: PlayerOwnership): number {
  return row.total_poolers > 0 ? Math.round((100 * row.owners) / row.total_poolers) : 0;
}

function ownershipFact(row: PlayerOwnership): OpportunityFact {
  return {
    label: 'Équipes qui l’ont',
    value: `${formatNumber(row.owners)} sur ${formatNumber(row.total_poolers)} (${ownershipShare(row)} %)`,
  };
}

function percentChange(current: number, previous: number): number | null {
  return previous === 0 ? null : ((current - previous) / previous) * 100;
}

type OpportunityInput = Omit<ContentOpportunity, 'score' | 'priority' | 'facts' | 'angle' | 'playerId' | 'playerName' | 'attribution'> & {
  facts?: OpportunityFact[];
  angle?: string | null;
  playerId?: number | null;
  playerName?: string | null;
  attribution?: string;
  volume: number;
  absoluteIncrease?: number;
  percentIncrease?: number | null;
  exceptional?: boolean;
  ownershipShare?: number | null;
};

function makeOpportunity(input: OpportunityInput, ctx: OpportunityContext): ContentOpportunity {
  const score = contentPriorityScore({
    volume: input.volume,
    absoluteIncrease: input.absoluteIncrease,
    percentIncrease: input.percentIncrease,
    exceptional: input.exceptional,
    eventTime: input.eventTime,
    ownershipShare: input.ownershipShare,
    now: ctx.now,
  });
  return {
    id: input.id,
    kind: input.kind,
    category: input.category,
    title: input.title,
    reason: input.reason,
    angle: input.angle ?? null,
    facts: input.facts ?? [],
    period: input.period,
    playerId: input.playerId ?? null,
    playerName: input.playerName ?? null,
    attribution: input.attribution ?? poolAttribution(ctx),
    sources: input.sources,
    eventTime: input.eventTime,
    score,
    priority: priorityLabel(score),
  };
}

/** Ajoute la détention (fait, angle, score) à une opportunité centrée sur un joueur. */
function withOwnership(
  input: OpportunityInput,
  ctx: OpportunityContext,
  angle: (row: PlayerOwnership) => string | null = defaultOwnershipAngle
): OpportunityInput {
  const row = ownershipOf(ctx, input.playerId ?? null);
  if (!row) return input;
  return {
    ...input,
    facts: [...(input.facts ?? []), ownershipFact(row)],
    angle: input.angle ?? angle(row),
    ownershipShare: ownershipShare(row),
    sources: [...input.sources, 'ci_get_player_ownership'],
  };
}

function defaultOwnershipAngle(row: PlayerOwnership): string | null {
  if (row.owners === 0) return 'Il ne figure actuellement dans aucune équipe du pool.';
  return `Il figure actuellement dans ${formatNumber(row.owners)} des ${formatNumber(row.total_poolers)} équipes du pool (${ownershipShare(row)} %), dont ${formatNumber(row.active_owners)} l’alignent.`;
}

/**
 * Joueur le plus signé, plus forte hausse et plus forte baisse.
 * `comparable` vaut false quand la période précédente commence avant la saison
 * (`previous_complete` de ci_get_engagement_comparison) : on garde le joueur le
 * plus signé, sans hausse ni baisse calculée sur une période incomplète.
 */
export function buildSigningOpportunities(
  rows: SigningPlayerComparison[],
  ctx: OpportunityContext,
  comparable = true
): ContentOpportunity[] {
  const phrases = PERIOD_PHRASES[ctx.window];
  const period = phrases.current;
  const sources = ['ci_get_signing_player_comparison'];
  const current = rows.filter((row) => row.current_count > 0);
  const result: ContentOpportunity[] = [];

  if (current.length) {
    const top = [...current].sort(
      (a, b) => b.current_count - a.current_count || a.player_id - b.player_id
    )[0];
    const name = playerLabel(top.player_name, top.player_id);
    const facts: OpportunityFact[] = [
      { label: 'Signatures', value: formatNumber(top.current_count) },
    ];
    if (comparable) {
      facts.push({ label: `Les ${phrases.previous}`, value: formatNumber(top.previous_count) });
    }
    result.push(
      makeOpportunity(
        withOwnership(
          {
            id: `most-signed-${ctx.window}-${top.player_id}`,
            kind: 'most-signed',
            category: 'signings',
            title: `${name}, le joueur le plus signé`,
            reason: `${name} a été mis sous contrat ${plural(top.current_count, 'fois', 'fois')} comme agent libre ${period}${comparable ? `, contre ${formatNumber(top.previous_count)} les ${phrases.previous}` : ''}.`,
            period,
            playerId: top.player_id,
            playerName: name,
            facts,
            sources,
            eventTime: top.latest_event_at,
            volume: top.current_count,
            absoluteIncrease: comparable ? Math.max(0, top.current_count - top.previous_count) : 0,
            percentIncrease: comparable ? percentChange(top.current_count, top.previous_count) : null,
          },
          ctx
        ),
        ctx
      )
    );
  }

  if (!comparable) return result;

  const changes = rows.map((row) => ({ row, difference: row.current_count - row.previous_count }));
  const rising = changes
    .filter(({ difference }) => difference > 0)
    .sort((a, b) => b.difference - a.difference || a.row.player_id - b.row.player_id)[0];
  if (rising) {
    const name = playerLabel(rising.row.player_name, rising.row.player_id);
    result.push(
      makeOpportunity(
        withOwnership(
          {
            id: `signings-rising-${ctx.window}-${rising.row.player_id}`,
            kind: 'signings-rising',
            category: 'signings',
            title: `${name}, la plus forte hausse de signatures`,
            reason: `${name} compte ${plural(rising.row.current_count, 'signature', 'signatures')} ${period}, contre ${formatNumber(rising.row.previous_count)} les ${phrases.previous} (+${formatNumber(rising.difference)}).`,
            period,
            playerId: rising.row.player_id,
            playerName: name,
            facts: [
              { label: 'Signatures', value: formatNumber(rising.row.current_count) },
              { label: 'Variation', value: `+${formatNumber(rising.difference)}` },
            ],
            sources,
            eventTime: rising.row.latest_event_at,
            volume: rising.row.current_count,
            absoluteIncrease: rising.difference,
            percentIncrease: percentChange(rising.row.current_count, rising.row.previous_count),
          },
          ctx
        ),
        ctx
      )
    );
  }

  const falling = changes
    .filter(({ difference }) => difference < 0)
    .sort((a, b) => a.difference - b.difference || a.row.player_id - b.row.player_id)[0];
  if (falling) {
    const name = playerLabel(falling.row.player_name, falling.row.player_id);
    result.push(
      makeOpportunity(
        withOwnership(
          {
            id: `signings-falling-${ctx.window}-${falling.row.player_id}`,
            kind: 'signings-falling',
            category: 'signings',
            title: `${name}, la plus forte baisse de signatures`,
            reason: `${name} compte ${plural(falling.row.current_count, 'signature', 'signatures')} ${period}, contre ${formatNumber(falling.row.previous_count)} les ${phrases.previous} (${formatNumber(falling.difference)}).`,
            period,
            playerId: falling.row.player_id,
            playerName: name,
            facts: [
              { label: 'Signatures', value: formatNumber(falling.row.current_count) },
              { label: 'Variation', value: formatNumber(falling.difference) },
            ],
            sources,
            eventTime: falling.row.latest_event_at,
            volume: falling.row.current_count,
          },
          ctx
        ),
        ctx
      )
    );
  }

  return result;
}

/** Joueur le plus signé depuis le début de la saison (fenêtre « Saison »). */
export function buildSeasonSigningOpportunity(
  top: TopPlayer | null,
  ctx: OpportunityContext
): ContentOpportunity[] {
  if (!top || !Number.isFinite(top.event_count) || top.event_count <= 0) return [];
  const name = playerLabel(top.player_name, top.player_id);
  return [
    makeOpportunity(
      withOwnership(
        {
          id: `most-signed-season-${top.player_id}`,
          kind: 'most-signed-season',
          category: 'signings',
          title: `${name}, le joueur le plus signé de la saison`,
          reason: `${name} a été mis sous contrat ${plural(top.event_count, 'fois', 'fois')} comme agent libre depuis le début de la saison.`,
          period: PERIOD_PHRASES.season.current,
          playerId: top.player_id,
          playerName: name,
          facts: [{ label: 'Signatures', value: formatNumber(top.event_count) }],
          sources: ['ci_get_top_players (metric=signed)'],
          eventTime: null,
          volume: top.event_count,
        },
        ctx
      ),
      ctx
    ),
  ];
}

/**
 * Joueur le plus libéré ou le plus placé en LTIR sur la période. Au moins deux
 * mouvements sont exigés : un mouvement isolé n'est pas un sujet.
 */
export function buildTopMoveOpportunity(
  top: TopPlayer | null,
  metric: 'dropped' | 'injured',
  ctx: OpportunityContext
): ContentOpportunity[] {
  if (!top || !Number.isFinite(top.event_count) || top.event_count < 2) return [];
  const name = playerLabel(top.player_name, top.player_id);
  const period = PERIOD_PHRASES[ctx.window].current;
  const released = metric === 'dropped';
  return [
    makeOpportunity(
      withOwnership(
        {
          id: `${released ? 'most-released' : 'most-ltir'}-${ctx.window}-${top.player_id}`,
          kind: released ? 'most-released' : 'most-ltir',
          category: 'roster',
          title: released
            ? `${name}, le joueur le plus libéré`
            : `${name}, le joueur le plus placé en LTIR`,
          reason: released
            ? `${name} a été libéré par ${plural(top.event_count, 'équipe', 'équipes')} ${period}.`
            : `${name} a été placé en LTIR par ${plural(top.event_count, 'équipe', 'équipes')} ${period}.`,
          period,
          playerId: top.player_id,
          playerName: name,
          facts: [
            { label: released ? 'Libérations' : 'Mises en LTIR', value: formatNumber(top.event_count) },
          ],
          sources: [`ci_get_top_players (metric=${metric})`],
          eventTime: null,
          volume: top.event_count,
        },
        ctx
      ),
      ctx
    ),
  ];
}

export interface RosterMoveTotals {
  releases: number;
  placed: number;
  reactivated: number;
}

/** Totaux de mouvements d'effectif à partir de la série quotidienne (fenêtre Saison). */
export function rosterMovesFromSeries(series: ActivityDayPoint[], days: number | null): RosterMoveTotals {
  const points = days === null ? series : series.slice(-days);
  return points.reduce(
    (acc, point) => ({
      releases: acc.releases + (point.drops ?? 0),
      placed: acc.placed + (point.ltir_placed ?? 0),
      reactivated: acc.reactivated + (point.ltir_reactivated ?? 0),
    }),
    { releases: 0, placed: 0, reactivated: 0 }
  );
}

/**
 * Totaux de la période : compteurs de ci_get_engagement_comparison pour 7/30/90
 * jours, série quotidienne pour la saison (la comparaison n'a pas de fenêtre saison).
 */
export function rosterMoveTotals(
  comparison: EngagementComparison | null,
  series: ActivityDayPoint[],
  window: TimeWindow
): RosterMoveTotals | null {
  if (window === 'season') return series.length ? rosterMovesFromSeries(series, null) : null;
  if (!comparison) return null;
  return {
    releases: comparison.releases_current ?? 0,
    placed: comparison.ltir_placed_current ?? 0,
    reactivated: comparison.ltir_reactivated_current ?? 0,
  };
}

/** Bilan des libérations et mouvements LTIR, sans comparaison (suivi récent). */
export function buildRosterMovesOpportunity(
  totals: RosterMoveTotals | null,
  ctx: OpportunityContext
): ContentOpportunity[] {
  if (!totals) return [];
  const total = totals.releases + totals.placed + totals.reactivated;
  if (total <= 0) return [];
  const period = PERIOD_PHRASES[ctx.window].current;
  return [
    makeOpportunity(
      {
        id: `roster-moves-${ctx.window}-${today(ctx)}`,
        kind: 'roster-moves',
        category: 'roster',
        title: 'Le bilan des mouvements d’effectif',
        reason: `Les équipes du pool ont fait ${plural(totals.releases, 'libération', 'libérations')}, ${plural(totals.placed, 'mise', 'mises')} en LTIR et ${plural(totals.reactivated, 'réactivation', 'réactivations')} ${period}.`,
        angle: `Ces mouvements sont suivis depuis le ${ROSTER_MOVES_TRACKED_SINCE}.`,
        period,
        facts: [
          { label: 'Libérations', value: formatNumber(totals.releases) },
          { label: 'Mises en LTIR', value: formatNumber(totals.placed) },
          { label: 'Réactivations', value: formatNumber(totals.reactivated) },
        ],
        sources: ['ci_get_engagement_comparison', 'ci_get_activity_timeseries'],
        eventTime: null,
        volume: total,
      },
      ctx
    ),
  ];
}

export function buildEngagementOpportunities(
  comparison: EngagementComparison | null,
  series: ActivityDayPoint[],
  ctx: OpportunityContext
): ContentOpportunity[] {
  const result: ContentOpportunity[] = [];
  const phrases = PERIOD_PHRASES[ctx.window];
  if (series.length) {
    const latest = series[series.length - 1];
    if (latest.signings > 0 || latest.new_joiners > 0) {
      const day = formatLongDay(latest.day);
      result.push(
        makeOpportunity(
          {
            id: `daily-summary-${latest.day}`,
            kind: 'daily-summary',
            category: 'activity',
            title: `Le pool le ${day}`,
            reason: `${plural(latest.signings, 'signature', 'signatures')} d’agents libres et ${plural(latest.new_joiners, 'nouvelle adhésion', 'nouvelles adhésions')} le ${day}.`,
            period: day,
            facts: [
              { label: 'Signatures', value: formatNumber(latest.signings) },
              { label: 'Nouvelles adhésions', value: formatNumber(latest.new_joiners) },
            ],
            sources: ['ci_get_activity_timeseries'],
            eventTime: null,
            volume: latest.signings + latest.new_joiners,
          },
          ctx
        )
      );
    }
  }

  const exceptional = detectExceptionalActivity(series);
  if (exceptional) {
    const point = series[series.length - 1];
    const day = formatLongDay(point.day);
    const average = formatNumber(Math.round(exceptional.baselineAverage * 10) / 10);
    result.push(
      makeOpportunity(
        {
          id: `exceptional-activity-${point.day}`,
          kind: 'exceptional-activity',
          category: 'activity',
          title: 'Une journée de signatures exceptionnelle',
          reason: `${plural(exceptional.current, 'signature', 'signatures')} d’agents libres le ${day}, au moins deux fois la moyenne des sept journées précédentes (${average} par jour).`,
          period: day,
          facts: [
            { label: 'Signatures du jour', value: formatNumber(exceptional.current) },
            { label: 'Moyenne des 7 jours précédents', value: average },
          ],
          sources: ['ci_get_activity_timeseries'],
          eventTime: null,
          volume: exceptional.current,
          exceptional: true,
        },
        ctx
      )
    );
  }

  if (!comparison) return result;

  if (
    comparison.previous_complete !== false &&
    detectPoolGrowth(comparison.new_poolers_current, comparison.new_poolers_previous)
  ) {
    const growth = comparison.new_poolers_current - comparison.new_poolers_previous;
    result.push(
      makeOpportunity(
        {
          id: `pool-growth-${ctx.window}-${today(ctx)}`,
          kind: 'pool-growth',
          category: 'activity',
          title: 'Le pool accueille plus de nouveaux poolers',
          reason: `${plural(comparison.new_poolers_current, 'nouveau pooler', 'nouveaux poolers')} ${phrases.current}, contre ${formatNumber(comparison.new_poolers_previous)} les ${phrases.previous} (+${formatNumber(growth)}).`,
          period: phrases.current,
          facts: [
            { label: 'Nouveaux poolers', value: formatNumber(comparison.new_poolers_current) },
            { label: `Les ${phrases.previous}`, value: formatNumber(comparison.new_poolers_previous) },
          ],
          sources: ['ci_get_engagement_comparison'],
          eventTime: null,
          volume: comparison.new_poolers_current,
          absoluteIncrease: growth,
          percentIncrease:
            comparison.new_poolers_previous > 0
              ? (100 * growth) / comparison.new_poolers_previous
              : null,
        },
        ctx
      )
    );
  }

  if (comparison.signings_current > 0 || comparison.new_poolers_current > 0) {
    result.push(
      makeOpportunity(
        {
          id: `period-summary-${ctx.window}-${today(ctx)}`,
          kind: 'period-summary',
          category: 'activity',
          title: ctx.window === '7d' ? 'La semaine dans le pool' : 'La période dans le pool',
          reason: `${plural(comparison.signings_current, 'signature', 'signatures')} d’agents libres par ${plural(comparison.poolers_signing_current, 'pooler', 'poolers')} et ${plural(comparison.new_poolers_current, 'nouvelle adhésion', 'nouvelles adhésions')} ${phrases.current}.`,
          period: phrases.current,
          facts: [
            { label: 'Signatures', value: formatNumber(comparison.signings_current) },
            { label: 'Poolers ayant signé', value: formatNumber(comparison.poolers_signing_current) },
            { label: 'Nouvelles adhésions', value: formatNumber(comparison.new_poolers_current) },
          ],
          sources: ['ci_get_engagement_comparison'],
          eventTime: null,
          volume: comparison.signings_current + comparison.new_poolers_current,
        },
        ctx
      )
    );
  }
  return result;
}

function injuryAngle(returned: boolean) {
  return (row: PlayerOwnership): string | null => {
    if (row.owners === 0) return null;
    const teams = `${formatNumber(row.owners)} des ${formatNumber(row.total_poolers)} équipes du pool (${ownershipShare(row)} %)`;
    return returned
      ? `Ce retour concerne ${teams}, dont ${formatNumber(row.ltir_owners)} l’ont placé en LTIR.`
      : `Cette absence concerne ${teams}, dont ${formatNumber(row.active_owners)} l’alignent actuellement.`;
  };
}

/** Une opportunité par blessure ou retour NHL d'un joueur présent dans un roster du pool. */
export function buildInjuryOpportunities(
  events: RosterInjuryNews[],
  ctx: OpportunityContext
): ContentOpportunity[] {
  return events.map((event) => {
    const name = playerLabel(event.player_name, event.player_id);
    const returned = event.event_type === 'return';
    const eventDay = formatLongDay(event.event_time.slice(0, 10));
    const detail = [event.status, event.description].filter(Boolean).join(' — ');
    const facts: OpportunityFact[] = [{ label: 'Date', value: eventDay }];
    if (event.status) facts.push({ label: 'Statut', value: event.status });
    return makeOpportunity(
      withOwnership(
        {
          id: `${returned ? 'return' : 'injury'}-${event.player_id}-${event.event_time}`,
          kind: returned ? 'injury-return' : 'injury',
          category: 'injuries',
          title: returned ? `${name} est de retour au jeu` : `${name} est blessé`,
          reason: `La LNH a signalé ${returned ? 'le retour' : 'la blessure'} de ${name} le ${eventDay}${detail ? ` : ${detail}` : ''}.`,
          period: eventDay,
          playerId: event.player_id,
          playerName: name,
          facts,
          attribution: poolAttribution(ctx, true),
          sources: [returned ? 'injury_return' : 'injury_player', 'ci_get_roster_injury_news'],
          eventTime: event.event_time,
          volume: 1,
        },
        ctx,
        injuryAngle(returned)
      ),
      ctx
    );
  });
}

/**
 * Bulletin de l'infirmerie : toutes les blessures et retours des sept derniers
 * jours, triés par nombre d'équipes touchées. Au moins deux événements.
 */
export function buildInjuryReportOpportunity(
  events: RosterInjuryNews[],
  ctx: OpportunityContext
): ContentOpportunity[] {
  if (events.length < 2) return [];
  const injuries = events.filter((event) => event.event_type === 'injury').length;
  const returns = events.length - injuries;
  const ranked = [...events].sort((a, b) => {
    const ownersA = ownershipOf(ctx, a.player_id)?.owners ?? 0;
    const ownersB = ownershipOf(ctx, b.player_id)?.owners ?? 0;
    return ownersB - ownersA || b.event_time.localeCompare(a.event_time);
  });
  const facts = ranked.slice(0, 5).map((event) => {
    const row = ownershipOf(ctx, event.player_id);
    const status = event.event_type === 'return' ? 'de retour' : event.status || 'blessé';
    return {
      label: playerLabel(event.player_name, event.player_id),
      value: row ? `${status}, ${plural(row.owners, 'équipe', 'équipes')}` : status,
    };
  });
  const maxShare = Math.max(
    0,
    ...events.map((event) => {
      const row = ownershipOf(ctx, event.player_id);
      return row ? ownershipShare(row) : 0;
    })
  );
  const latest = events.reduce((acc, event) => (event.event_time > acc ? event.event_time : acc), events[0].event_time);
  return [
    makeOpportunity(
      {
        id: `injury-report-${today(ctx)}`,
        kind: 'injury-report',
        category: 'injuries',
        title: 'L’infirmerie de la semaine',
        reason: `La LNH a signalé ${plural(injuries, 'blessure', 'blessures')} et ${plural(returns, 'retour', 'retours')} au jeu ces 7 derniers jours parmi les joueurs des équipes du pool.`,
        angle: ctx.ownership ? 'Joueurs classés selon le nombre d’équipes du pool qui les ont.' : null,
        period: 'ces 7 derniers jours',
        facts,
        attribution: poolAttribution(ctx, true),
        sources: ['ci_get_roster_injury_news', ...(ctx.ownership ? ['ci_get_player_ownership'] : [])],
        eventTime: latest,
        volume: events.length,
        ownershipShare: maxShare,
      },
      ctx
    ),
  ];
}

/** Trie par score décroissant, puis par titre. */
export function sortOpportunities(opportunities: ContentOpportunity[]): ContentOpportunity[] {
  return [...opportunities].sort((a, b) => b.score - a.score || a.title.localeCompare(b.title, 'fr'));
}

/** Place les sujets d'activité du pool en tête, sans changer l'ordre à l'intérieur des groupes. */
export function activityFirst(opportunities: ContentOpportunity[]): ContentOpportunity[] {
  return [
    ...opportunities.filter((opportunity) => opportunity.category === 'activity'),
    ...opportunities.filter((opportunity) => opportunity.category !== 'activity'),
  ];
}

function factLines(facts: OpportunityFact[]): string {
  return facts.map((fact) => `– ${fact.label} : ${fact.value}`).join('\n');
}

/** Coupe proprement au dernier mot complet sous la limite. */
export function truncateText(text: string, limit: number): string {
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:–—-]+$/, '')}…`;
}

function shortText(opportunity: ContentOpportunity, leagueName?: string | null): string {
  const pool = leagueName?.trim() ? ` (pool ${leagueName.trim()})` : '';
  const full = `${opportunity.reason}${opportunity.angle ? ` ${opportunity.angle}` : ''}`;
  const withPool = `${full}${pool}`;
  if (withPool.length <= SHORT_FORMAT_LIMIT) return withPool;
  const reasonOnly = `${opportunity.reason}${pool}`;
  if (reasonOnly.length <= SHORT_FORMAT_LIMIT) return reasonOnly;
  return truncateText(opportunity.reason, SHORT_FORMAT_LIMIT);
}

/** Texte prêt à publier. Aucun nom de RPC, aucun emoji, aucun hashtag. */
export function formatOpportunity(
  opportunity: ContentOpportunity,
  format: ContentFormat,
  leagueName?: string | null
): string {
  const angle = opportunity.angle ? ` ${opportunity.angle}` : '';
  switch (format) {
    case 'short':
      return shortText(opportunity, leagueName);
    case 'social':
      return [
        opportunity.title,
        `${opportunity.reason}${angle}`,
        opportunity.facts.length ? factLines(opportunity.facts) : null,
        opportunity.attribution,
      ]
        .filter(Boolean)
        .join('\n\n');
    case 'article':
      return [
        opportunity.title,
        `${opportunity.reason}${angle}`,
        opportunity.facts.length ? `En chiffres\n${factLines(opportunity.facts)}` : null,
        `Période : ${opportunity.period}. ${CATEGORY_METHOD[opportunity.category]}`,
        opportunity.attribution,
      ]
        .filter(Boolean)
        .join('\n\n');
    case 'newsletter':
      return `${opportunity.title}. ${opportunity.reason}${angle} ${opportunity.attribution}`;
  }
}

/** Sujets de regroupement dont le titre seul n'apprend rien : exclus du bulletin. */
const DIGEST_EXCLUDED_KINDS = new Set(['injury-report']);

/**
 * Sélection du bulletin : meilleurs scores, au plus deux sujets par catégorie
 * et un seul sujet par joueur.
 */
export function selectDigest(opportunities: ContentOpportunity[], limit = 5): ContentOpportunity[] {
  const perCategory = new Map<OpportunityCategory, number>();
  const players = new Set<number>();
  const selected: ContentOpportunity[] = [];
  for (const opportunity of sortOpportunities(opportunities)) {
    if (selected.length >= limit) break;
    if (DIGEST_EXCLUDED_KINDS.has(opportunity.kind)) continue;
    const count = perCategory.get(opportunity.category) ?? 0;
    if (count >= 2) continue;
    if (opportunity.playerId !== null && players.has(opportunity.playerId)) continue;
    selected.push(opportunity);
    perCategory.set(opportunity.category, count + 1);
    if (opportunity.playerId !== null) players.add(opportunity.playerId);
  }
  return selected;
}

/** Bulletin regroupant plusieurs sujets, dans le format demandé. */
export function buildDigest(
  opportunities: ContentOpportunity[],
  format: ContentFormat,
  ctx: Pick<OpportunityContext, 'window' | 'leagueName'>
): string {
  const items = selectDigest(opportunities);
  if (!items.length) return '';
  const pool = ctx.leagueName?.trim() ? `le pool ${ctx.leagueName.trim()}` : 'le pool';
  const period = PERIOD_PHRASES[ctx.window].current;
  const title = `Ce qu’il faut retenir dans ${pool} ${period}`;
  const hasNhl = items.some((item) => item.category === 'injuries');
  const attribution = hasNhl
    ? poolAttribution({ window: ctx.window, leagueName: ctx.leagueName }, true)
    : poolAttribution({ window: ctx.window, leagueName: ctx.leagueName });
  switch (format) {
    case 'short':
      return truncateText(
        `${title} : ${items.map((item) => item.title).join(' ; ')}.`,
        SHORT_FORMAT_LIMIT
      );
    case 'social':
      return [title, items.map((item) => `– ${item.reason}`).join('\n'), attribution].join('\n\n');
    case 'article':
      return [
        title,
        ...items.map((item) => `${item.title}\n${item.reason}${item.angle ? ` ${item.angle}` : ''}`),
        attribution,
      ].join('\n\n');
    case 'newsletter':
      return `${title}. ${items.map((item) => item.reason).join(' ')} ${attribution}`;
  }
}

/** Ancienneté lisible d'un événement (« il y a 3 h »), null si inconnue. */
export function formatFreshness(eventTime: string | null, now = Date.now()): string | null {
  if (!eventTime) return null;
  const time = new Date(eventTime).getTime();
  if (!Number.isFinite(time)) return null;
  const minutes = Math.floor((now - time) / 60000);
  if (minutes < 0) return null;
  if (minutes < 2) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} jour${days > 1 ? 's' : ''}`;
}

function normalizeSearch(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/** Recherche insensible aux accents dans le joueur et le titre. */
export function matchesSearch(opportunity: ContentOpportunity, query: string): boolean {
  const needle = normalizeSearch(query);
  if (!needle) return true;
  return normalizeSearch(`${opportunity.playerName ?? ''} ${opportunity.title}`).includes(needle);
}
